import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { type Tone, toneText } from "@/lib/semantics";
import InfoTooltip from "./InfoTooltip";

interface StatProps {
  /** Small uppercase caption. */
  label: ReactNode;
  /** The value — already formatted (numbers come pre-stringified for mono). */
  value: ReactNode;
  /** Colour the value per SPEC §14. Defaults to neutral foreground. */
  tone?: Tone;
  /** Optional pedagogy tooltip beside the label. */
  help?: ReactNode;
  /** Optional sub-line under the value. */
  hint?: ReactNode;
  className?: string;
  valueClassName?: string;
  /** Size of the value text. */
  size?: "sm" | "md" | "lg";
  testId?: string;
}

const SIZE: Record<NonNullable<StatProps["size"]>, string> = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-2xl tracking-tight",
};

/**
 * A compact label/value stat. The value uses the tabular monospace face so
 * real-time refreshes don't shift layout (DIRECTION ARTISTIQUE).
 */
export default function Stat({
  label,
  value,
  tone = "neutral",
  help,
  hint,
  className,
  valueClassName,
  size = "lg",
  testId,
}: StatProps) {
  return (
    <div className={cn("flex flex-col gap-0.5", className)} data-testid={testId}>
      <span className="text-muted-foreground flex items-center gap-1 text-[0.68rem] font-medium tracking-wide uppercase [letter-spacing:0.06em]">
        {label}
        {help != null && <InfoTooltip>{help}</InfoTooltip>}
      </span>
      <span
        className={cn(
          "font-mono font-semibold tabular-nums",
          SIZE[size],
          toneText(tone),
          valueClassName,
        )}
      >
        {value}
      </span>
      {hint != null && (
        <span className="text-muted-foreground text-xs">{hint}</span>
      )}
    </div>
  );
}
